import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { UserCheck, ChevronLeft, CheckCircle, Mail } from 'lucide-react';

const PrivacyRequestsPage = () => {
  const rights = [
    "Access the personal data we hold about you",
    "Correct inaccurate or incomplete data",
    "Request deletion of your personal data",
    "Restrict processing of your data",
    "Object to processing based on legitimate interests",
    "Request data portability (for data provided under consent or contract)"
  ];

  return (
    <div className="min-h-screen bg-black text-apple-100 py-24">
      <Helmet>
        <title>Privacy Requests (EEA/UK) - Sortio</title>
        <meta name="description" content="How to exercise your privacy rights under GDPR for Sortio by CMG Labs." />
      </Helmet>

      {/* Background gradient */}
      <div className="absolute top-0 inset-x-0 h-[500px] bg-gradient-to-b from-apple-700/10 to-transparent"></div>
      <div className="absolute -top-[600px] -right-[300px] w-[1000px] h-[1000px] bg-sortio-primary/5 rounded-full blur-3xl"></div>

      <div className="container mx-auto px-6 max-w-4xl relative z-10">
        {/* Back button */}
        <Link to="/" className="inline-flex items-center text-apple-300 hover:text-sortio-primary mb-12 transition-colors group">
          <ChevronLeft className="w-4 h-4 mr-1 group-hover:-translate-x-1 transition-transform" />
          <span>Back to Home</span>
        </Link>

        {/* Header */}
        <div className="mb-16 flex flex-col items-center">
          <div className="w-14 h-14 rounded-full bg-apple-700/60 backdrop-blur-sm flex items-center justify-center mb-6 border border-apple-600/30">
            <UserCheck className="w-6 h-6 text-sortio-primary" />
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-center">
            Privacy <span className="apple-gradient-text">Requests</span>
          </h1>
          <p className="text-apple-300 mt-4 text-center max-w-xl">
            Exercise your data protection rights under GDPR (EEA/UK).
          </p>
        </div>

        {/* Content */}
        <div className="apple-panel p-8 md:p-12 rounded-apple backdrop-blur-sm mb-12">
          <div className="space-y-8">
            <section className="space-y-4">
              <h2 className="text-xl font-medium text-apple-50">Your rights</h2>
              <p className="text-apple-300 leading-relaxed">
                If you are located in the European Economic Area (EEA) or United Kingdom (UK), you have the following rights regarding your personal data under the GDPR and UK GDPR:
              </p>
              <ul className="space-y-2">
                {rights.map((right, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-sortio-secondary mr-3 mt-0.5 flex-shrink-0" aria-hidden="true" />
                    <span className="text-apple-300">{right}</span>
                  </li>
                ))}
              </ul>
              <p className="text-apple-300 leading-relaxed text-sm">
                US residents: see <Link to="/privacy/choices" className="text-sortio-primary hover:text-sortio-primary/80">Your Privacy Choices</Link> for CCPA, CPRA, and equivalent state-law rights (including opt-out of sale and sharing, limit-use, and non-discrimination).
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-medium text-apple-50">How to make a request</h2>
              <p className="text-apple-300 leading-relaxed">
                All data-subject requests are handled by email. Access, portability, correction, restriction, and objection requests are fulfilled manually so we can verify your identity against the email on your account before releasing or modifying personal data. Account deletion can also be initiated in-app under <strong>Settings, Account, Delete account</strong>, which uses a two-step email-confirmed flow.
              </p>
              <div className="p-6 rounded-lg bg-sortio-primary/10 border border-sortio-primary/30">
                <div className="flex items-start">
                  <Mail className="w-6 h-6 text-sortio-primary mr-4 mt-1 flex-shrink-0" />
                  <div className="space-y-2">
                    <p className="text-apple-100 font-medium">Email us from your registered address</p>
                    <p className="text-apple-300 text-sm">
                      Send your request to{' '}
                      <a
                        href="mailto:marcus@getsortio.com"
                        className="text-sortio-primary hover:text-sortio-primary/80 font-medium"
                      >
                        marcus@getsortio.com
                      </a>
                      {' '}from the email address linked to your Sortio account.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-medium text-apple-50">What to expect</h2>
              <div className="space-y-3 text-apple-300">
                <div className="flex items-start">
                  <div className="w-8 h-8 rounded-full bg-sortio-secondary/20 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">
                    <span className="text-sortio-secondary font-medium text-sm">1</span>
                  </div>
                  <div>
                    <h3 className="text-apple-50 font-medium mb-1">Identity verification</h3>
                    <p className="text-sm">Before fulfilling deletion, correction, or export requests, we confirm that the request comes from the account owner. We may ask you to reply from the email address on file, click a one-time verification link, or provide the last four digits of the payment method on the account. We never ask for full payment details or government identifiers.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-8 h-8 rounded-full bg-sortio-secondary/20 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">
                    <span className="text-sortio-secondary font-medium text-sm">2</span>
                  </div>
                  <div>
                    <h3 className="text-apple-50 font-medium mb-1">Response timeframe</h3>
                    <p className="text-sm">
                      <strong>GDPR / UK GDPR:</strong> we respond within <strong>one month (30 days)</strong> of receiving a verified request. In complex cases, this may be extended by up to two additional months and we will notify you of any such extension.
                    </p>
                    <p className="text-sm mt-1">
                      <strong>CCPA / CPRA:</strong> we respond within <strong>45 days</strong>, extendable once for an additional 45 days with notice.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-8 h-8 rounded-full bg-sortio-secondary/20 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">
                    <span className="text-sortio-secondary font-medium text-sm">3</span>
                  </div>
                  <div>
                    <h3 className="text-apple-50 font-medium mb-1">Data deletion propagation</h3>
                    <p className="text-sm">Where applicable, deletions are propagated to our processors as outlined in our <Link to="/sub-processors" className="text-sortio-primary hover:text-sortio-primary/80">Sub-processors</Link> page.</p>
                  </div>
                </div>
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-medium text-apple-50">Data portability</h2>
              <p className="text-apple-300 leading-relaxed">
                For data portability requests, we will provide your data in a commonly used, machine-readable format (typically JSON). This applies to data you provided to us under consent or as part of our contract with you.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-medium text-apple-50">Withdrawing consent</h2>
              <p className="text-apple-300 leading-relaxed">
                Where we process your data based on consent (such as optional diagnostics or marketing communications), you can withdraw your consent at any time by contacting us at{' '}
                <a href="mailto:marcus@getsortio.com" className="text-sortio-primary hover:text-sortio-primary/80">
                  marcus@getsortio.com
                </a>
                {' '}or adjusting your settings in the app under <strong>Settings ▸ Privacy</strong>.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-medium text-apple-50">Lodge a complaint</h2>
              <p className="text-apple-300 leading-relaxed">
                If you believe we have not adequately addressed your privacy concerns, you have the right to lodge a complaint with your local data protection supervisory authority in the EEA or the Information Commissioner's Office (ICO) in the UK.
              </p>
            </section>
          </div>
        </div>

        {/* Back to top button */}
        <div className="flex justify-center mb-8">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="inline-flex items-center px-4 py-2 rounded-full bg-apple-700/40 border border-apple-600/30 text-sm text-apple-300 hover:text-sortio-primary transition-colors"
          >
            <svg className="w-4 h-4 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m18 15-6-6-6 6"/>
            </svg>
            <span>Back to top</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrivacyRequestsPage;
